import React from 'react';
import { AlertCircle, TrendingUp } from 'lucide-react';
import { WeibullAnalysis } from '../../types/analytics.types';
import { interpretBeta } from '../../utils/weibullMath';

interface WeibullResultsProps {
    analysis: Partial<WeibullAnalysis>;
}

export const WeibullResults: React.FC<WeibullResultsProps> = ({ analysis }) => {
    return (
        <div className="space-y-6">
            {/* Parameters */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Weibull Parameters</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-sm text-blue-600 mb-1">Shape Parameter (β)</div>
                        <div className="text-3xl font-bold text-blue-900">{analysis.parameters?.beta.toFixed(2)}</div>
                        <div className="text-xs text-blue-700 mt-2">{interpretBeta(analysis.parameters?.beta || 0)}</div>
                        <div className="text-xs text-gray-600 mt-1">
                            CI: [{analysis.lowerBoundBeta?.toFixed(2)}, {analysis.upperBoundBeta?.toFixed(2)}]
                        </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-sm text-green-600 mb-1">Scale Parameter (η)</div>
                        <div className="text-3xl font-bold text-green-900">{analysis.parameters?.eta.toFixed(0)} hrs</div>
                        <div className="text-xs text-green-700 mt-2">Characteristic Life</div>
                        <div className="text-xs text-gray-600 mt-1">
                            CI: [{analysis.lowerBoundEta?.toFixed(0)}, {analysis.upperBoundEta?.toFixed(0)}]
                        </div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="text-sm text-purple-600 mb-1">Goodness of Fit (R²)</div>
                        <div className="text-3xl font-bold text-purple-900">{(analysis.parameters?.rSquared || 0).toFixed(1)}%</div>
                        <div className="text-xs text-purple-700 mt-2">
                            {analysis.parameters && analysis.parameters.rSquared > 0.95
                                ? 'Excellent fit'
                                : analysis.parameters && analysis.parameters.rSquared > 0.90
                                    ? 'Good fit'
                                    : 'Fair fit'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Life Predictions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Life Predictions
                </h2>
                <div className="grid md:grid-cols-4 gap-4">
                    <div className="p-4 border-l-4 border-red-500 bg-gray-50">
                        <div className="text-sm text-gray-600 mb-1">B10 Life</div>
                        <div className="text-2xl font-bold text-gray-900">{analysis.b10Life?.toFixed(0)} hrs</div>
                        <div className="text-xs text-gray-500 mt-1">10% will fail by this time</div>
                    </div>
                    <div className="p-4 border-l-4 border-yellow-500 bg-gray-50">
                        <div className="text-sm text-gray-600 mb-1">B50 Life (Median)</div>
                        <div className="text-2xl font-bold text-gray-900">{analysis.b50Life?.toFixed(0)} hrs</div>
                        <div className="text-xs text-gray-500 mt-1">50% will fail by this time</div>
                    </div>
                    <div className="p-4 border-l-4 border-green-500 bg-gray-50">
                        <div className="text-sm text-gray-600 mb-1">B90 Life</div>
                        <div className="text-2xl font-bold text-gray-900">{analysis.b90Life?.toFixed(0)} hrs</div>
                        <div className="text-xs text-gray-500 mt-1">90% will fail by this time</div>
                    </div>
                    <div className="p-4 border-l-4 border-blue-500 bg-gray-50">
                        <div className="text-sm text-gray-600 mb-1">Mean Life (MTTF)</div>
                        <div className="text-2xl font-bold text-gray-900">{analysis.meanLife?.toFixed(0)} hrs</div>
                        <div className="text-xs text-gray-500 mt-1">Average life expectancy</div>
                    </div>
                </div>
            </div>

            {/* Recommendations */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">Recommendations</h3>
                        <ul className="space-y-2 text-blue-800 text-sm">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-0.5">•</span>
                                <span>
                                    <strong>Preventive Maintenance:</strong> Schedule maintenance at {(analysis.b10Life! * 0.8).toFixed(0)} hours
                                    (80% of B10 life) to minimize failures
                                </span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-0.5">•</span>
                                <span>
                                    <strong>Warranty Period:</strong> Consider setting warranty at {(analysis.b10Life! * 0.5).toFixed(0)} hours
                                    (50% of B10 life) for low failure risk
                                </span>
                            </li>
                            {analysis.parameters && analysis.parameters.beta < 1 && (
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-600 mt-0.5">•</span>
                                    <span>
                                        <strong>Infant Mortality Detected:</strong> Consider burn-in testing or improved quality controls
                                    </span>
                                </li>
                            )}
                            {analysis.parameters && analysis.parameters.beta > 3 && (
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-600 mt-0.5">•</span>
                                    <span>
                                        <strong>Rapid Wear-Out:</strong> Investigation recommended - may indicate design or material issues
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
